import { View, Text, styled, useTheme } from 'tamagui'
import { useEffect } from 'react'
import { AlertTriangle } from '@tamagui/lucide-icons'
import { useAppStore } from '@/store'
import { messages } from '@/mocks'

const Overlay = styled(View, {
  position: 'absolute' as any,
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 4000,
})

const ModalCard = styled(View, {
  backgroundColor: '$surfaceRaised',
  borderRadius: '$borderRadiusMd',
  borderWidth: 1,
  borderColor: '$borderDefault',
  width: 440,
  maxWidth: '90%' as any,
  overflow: 'hidden',
})

const ModalHeader = styled(View, {
  flexDirection: 'row',
  alignItems: 'center',
  gap: 10,
  paddingHorizontal: 20,
  paddingTop: 20,
  paddingBottom: 8,
})

const ModalTitle = styled(Text, {
  fontFamily: '$fontHeading',
  fontWeight: '600',
  fontSize: 18,
  color: '$surfaceForeground',
})

const ModalBody = styled(View, {
  paddingHorizontal: 20,
  paddingBottom: 16,
})

const BodyText = styled(Text, {
  fontFamily: '$fontBody',
  fontSize: 14,
  color: '$sidebarMuted',
  lineHeight: 20,
  marginBottom: 12,
})

const PreviewCard = styled(View, {
  backgroundColor: '$sidebar',
  borderRadius: '$borderRadiusSm',
  borderWidth: 1,
  borderColor: '$borderDefault',
  paddingHorizontal: 12,
  paddingVertical: 8,
})

const PreviewText = styled(Text, {
  fontFamily: '$fontMono',
  fontSize: 13,
  color: '$sidebarMuted',
  lineHeight: 20,
  // @ts-expect-error -- web text overflow
  style: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
})

const ModalFooter = styled(View, {
  flexDirection: 'row',
  justifyContent: 'flex-end',
  gap: 8,
  paddingHorizontal: 20,
  paddingVertical: 16,
  backgroundColor: '$sidebar',
})

const CancelButton = styled(View, {
  paddingHorizontal: 16,
  paddingVertical: 8,
  borderRadius: '$borderRadiusSm',
  cursor: 'pointer',
  hoverStyle: {
    backgroundColor: '$sidebarHover',
  },
  // @ts-expect-error -- web transition
  style: { transition: 'background-color 100ms ease' },
})

const CancelText = styled(Text, {
  fontFamily: '$fontBody',
  fontSize: 14,
  color: '$surfaceForeground',
})

const DeleteButton = styled(View, {
  paddingHorizontal: 16,
  paddingVertical: 8,
  borderRadius: '$borderRadiusSm',
  backgroundColor: '$error',
  cursor: 'pointer',
  hoverStyle: {
    opacity: 0.9,
  },
  // @ts-expect-error -- web transition
  style: { transition: 'opacity 100ms ease' },
})

const DeleteText = styled(Text, {
  fontFamily: '$fontBody',
  fontSize: 14,
  fontWeight: '600',
  color: 'white',
})

export function MessageDeleteConfirm() {
  const { deleteMessageId, setDeleteMessageId } = useAppStore()

  useEffect(() => {
    if (!deleteMessageId) return

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setDeleteMessageId(null)
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [deleteMessageId, setDeleteMessageId])

  if (!deleteMessageId) return null

  // Find the message content for preview
  let messageContent = ''
  for (const channelMessages of Object.values(messages)) {
    const found = channelMessages.find((m) => m.id === deleteMessageId)
    if (found) {
      messageContent = found.content
      break
    }
  }

  const handleDelete = () => {
    // In a real app, this would call an API. For mock, just close.
    // Remove from local mock data
    for (const [channelId, channelMessages] of Object.entries(messages)) {
      const index = channelMessages.findIndex((m) => m.id === deleteMessageId)
      if (index !== -1) {
        channelMessages.splice(index, 1)
        break
      }
    }
    setDeleteMessageId(null)
  }

  return (
    <Overlay
      // @ts-expect-error -- web fixed positioning
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      onPress={() => setDeleteMessageId(null)}
    >
      <ModalCard onPress={(e: any) => e.stopPropagation()}>
        <ModalHeader>
          <AlertTriangle size={20} color="$error" />
          <ModalTitle>Delete Message</ModalTitle>
        </ModalHeader>

        <ModalBody>
          <BodyText>
            Are you sure you want to delete this message? This action cannot be undone.
          </BodyText>
          {messageContent && (
            <PreviewCard>
              <PreviewText>{messageContent}</PreviewText>
            </PreviewCard>
          )}
        </ModalBody>

        <ModalFooter>
          <CancelButton onPress={() => setDeleteMessageId(null)}>
            <CancelText>Cancel</CancelText>
          </CancelButton>
          <DeleteButton onPress={handleDelete}>
            <DeleteText>Delete</DeleteText>
          </DeleteButton>
        </ModalFooter>
      </ModalCard>
    </Overlay>
  )
}
